import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import API from "../api/api";

const TLS_URL = "https://visas-de.tlscontact.com/en-us";
const TLS_CONFIRM_XPATH = "//a[@id='btn-confirm-country']/span";

const COUNTRY_OPTIONS = [
  { value: "am", label: "Armenia" },
  { value: "az", label: "Azerbaijan" },
  { value: "bw", label: "Botswana" },
  { value: "eg", label: "Egypt" },
  { value: "sz", label: "Eswatini" },
  { value: "ir", label: "Islamic Republic of Iran" },
  { value: "kz", label: "Kazakhstan" },
  { value: "ke", label: "Kenya" },
  { value: "kg", label: "Kyrgyzstan" },
  { value: "ls", label: "Lesotho" },
  { value: "ly", label: "Libya" },
  { value: "mu", label: "Mauritius" },
  { value: "ma", label: "Morocco" },
  { value: "na", label: "Namibia" },
  { value: "sa", label: "Saudi Arabia" },
  { value: "za", label: "South Africa" },
  { value: "tj", label: "Tajikistan" },
  { value: "tz", label: "Tanzania" },
  { value: "tn", label: "Tunisia" },
  { value: "ug", label: "Uganda" },
  { value: "gb", label: "United Kingdom" },
  { value: "uz", label: "Uzbekistan" },
];

const COUNTRY_CENTRE_OPTIONS = {
  gb: [
    { value: "London", label: "London", description: "18 Ryeland Boulevard, Wandsworth, London" },
    { value: "Manchester", label: "Manchester", description: "1 Portland Street, Manchester" },
    { value: "Edinburgh", label: "Edinburgh", description: "Argyle House, 3 Lady Lawson Street, Edinburgh" },
  ],
};

const TLSContactPage = () => {
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedCentreLabel, setSelectedCentreLabel] = useState("");
  const [manualCentreLabel, setManualCentreLabel] = useState("");
  const [bridgeStatus, setBridgeStatus] = useState({ extensionClients: 0, frontendClients: 0 });
  const [statusLoading, setStatusLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  const selectedCountry = useMemo(
    () => COUNTRY_OPTIONS.find((option) => option.value === selectedCountryCode) || null,
    [selectedCountryCode]
  );

  const availableCentres = useMemo(
    () => COUNTRY_CENTRE_OPTIONS[selectedCountryCode] || [],
    [selectedCountryCode]
  );

  const resolvedCentreLabel = useMemo(() => {
    if (availableCentres.length > 0) {
      return selectedCentreLabel;
    }

    return manualCentreLabel.trim();
  }, [availableCentres.length, manualCentreLabel, selectedCentreLabel]);

  useEffect(() => {
    setSelectedCentreLabel("");
    setManualCentreLabel("");
  }, [selectedCountryCode]);

  useEffect(() => {
    let ignore = false;

    async function loadBridgeStatus() {
      try {
        const response = await API.get("/extension-bridge/status");
        if (!ignore) {
          setBridgeStatus({
            extensionClients: response.data?.extensionClients || 0,
            frontendClients: response.data?.frontendClients || 0,
          });
        }
      } catch (_error) {
        if (!ignore) {
          setMessage({
            type: "warning",
            text: "Backend bridge status could not be loaded. You can still try sending the TLS payload.",
          });
        }
      } finally {
        if (!ignore) {
          setStatusLoading(false);
        }
      }
    }

    loadBridgeStatus();
    return () => {
      ignore = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedCountry) {
      setMessage({ type: "danger", text: "Please select a TLS country before sending." });
      return;
    }

    if (!resolvedCentreLabel) {
      setMessage({
        type: "danger",
        text:
          availableCentres.length > 0
            ? "Please select a TLS centre before sending."
            : "Please enter the TLS centre name before sending.",
      });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const response = await API.post("/extension-bridge/push", {
        serviceType: "TLS",
        tlsUrl: TLS_URL,
        tlsCountryCode: selectedCountry.value,
        tlsCountryLabel: selectedCountry.label,
        tlsCentreLabel: resolvedCentreLabel,
        tlsConfirmXPath: TLS_CONFIRM_XPATH,
      });

      const delivered = response.data?.bridge?.delivered || 0;
      setBridgeStatus((prev) => ({
        ...prev,
        extensionClients: response.data?.bridge?.connectedClients ?? prev.extensionClients,
      }));

      if (delivered > 0) {
        setMessage({
          type: "success",
          text: `TLS payload sent to ${delivered} connected extension client(s). The TLS extension should confirm ${selectedCountry.label} and continue with ${resolvedCentreLabel}.`,
        });
      } else {
        setMessage({
          type: "info",
          text: "TLS payload was queued by the backend. If the TLS extension is loaded, it will pick up the task on its next poll even when no websocket client is currently shown as connected.",
        });
      }
    } catch (error) {
      const missing = error.response?.data?.missing;
      if (Array.isArray(missing) && missing.length > 0) {
        setMessage({
          type: "danger",
          text: `TLS payload is missing required fields: ${missing.join(", ")}`,
        });
      } else {
        setMessage({
          type: "danger",
          text: error.response?.data?.message || "Failed to send TLS payload to the extension bridge.",
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="TLS Form"
        description="Send the selected TLS country to the extension so it can open TLS Contact, select the country, and confirm."
        actions={
          <div className="card" style={{ minWidth: "240px" }}>
            <div className="card-body py-3 px-3">
              <div className="small text-uppercase text-muted fw-semibold mb-2">Bridge Status</div>
              <div className="d-flex justify-content-between mb-2">
                <span>Extension clients</span>
                <strong>{statusLoading ? "Checking..." : bridgeStatus.extensionClients}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Frontend clients</span>
                <strong>{statusLoading ? "Checking..." : bridgeStatus.frontendClients}</strong>
              </div>
            </div>
          </div>
        }
      />

      <div className="card">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-lg-7">
                <label className="form-label fw-semibold">TLS Country</label>
                <select
                  className="form-select"
                  value={selectedCountryCode}
                  onChange={(event) => setSelectedCountryCode(event.target.value)}
                >
                  <option value="">Choose from</option>
                  {COUNTRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-lg-5">
                <label className="form-label fw-semibold">TLS URL</label>
                <input className="form-control" value={TLS_URL} readOnly />
              </div>
            </div>

            <div className="mt-4">
              <label className="form-label fw-semibold">TLS Centre</label>

              {availableCentres.length > 0 ? (
                <div className="row g-3">
                  {availableCentres.map((centre) => {
                    const isActive = selectedCentreLabel === centre.value;

                    return (
                      <div className="col-md-4" key={centre.value}>
                        <button
                          type="button"
                          className={`w-100 text-start border rounded-3 p-3 ${
                            isActive ? "border-primary bg-primary-subtle" : "bg-white"
                          }`}
                          onClick={() => setSelectedCentreLabel(centre.value)}
                          style={{ transition: "all 0.2s ease" }}
                        >
                          <div className="fw-semibold mb-1">{centre.label}</div>
                          <div className="small text-muted">{centre.description}</div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <input
                    className="form-control"
                    value={manualCentreLabel}
                    onChange={(event) => setManualCentreLabel(event.target.value)}
                    placeholder="Type the TLS centre name exactly as it appears on the website"
                  />
                  <div className="form-text">
                    Preloaded centre options are currently available for the United Kingdom. For other
                    countries, enter the centre label manually.
                  </div>
                </>
              )}
            </div>

            <div
              className="mt-4 p-3 rounded-3"
              style={{ background: "var(--vp-primary-soft)", border: "1px solid var(--vp-primary-muted)" }}
            >
              <div className="fw-semibold mb-2">Automation Step Included</div>
              <div className="text-muted small">
                Open TLS Contact, select the frontend country value in `#select-country`, click the
                confirm button using `{TLS_CONFIRM_XPATH}`, then find the matching centre card and press
                its Continue button.
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending ? "Sending..." : "Send To TLS Extension"}
              </button>
            </div>
          </form>

          {message && <div className={`alert alert-${message.type} mt-4 mb-0`}>{message.text}</div>}
        </div>
      </div>
    </AppShell>
  );
};

export default TLSContactPage;